import Vue from "vue"
import Router from "vue-router"
import VueMeta from "vue-meta"
import { GAME_KEYS } from "@/store/game/game.constants"
import store from "@/store/index.store"
import { ROUTER_NAMES } from "@/constants/router.constants"

// Layouts
import { FooterLayout, MainLayout } from "@/layouts/index"

// Module Routes
import authRoutes from "@/router/auth.routes"
import bootcampRoutes from "@/router/bootcamp.routes"
import coachHubRoutes from "@/router/user-accounts/coach.routes"
import coachingRoutes from "@/router/coaching.routes"
import companyRoutes from "@/router/company.routes"
import coursesRoutes from "@/router/courses.routes"
import landingRoutes from "@/router/landing.routes"
import leagueOfLegendsRoutes from "@/router/leagueoflegends.routes"
import ssbuRoutes from "@/router/ssbu.routes"
import storeRoutes from "@/router/store.routes"
import userRoutes from "@/router/user-accounts/user.routes"
import { MIXINS } from "@/utils/mixins"

// General Pages
const Home = () => import(/* webpackChunkName: "home" */ "@/pages/Home")
const Dashboard = () =>
  import(/* webpackChunkName: "dashboard" */ "@/pages/Dashboard")
const Page404 = () => import(/* webpackChunkName: "error" */ "@/pages/Page404")

Vue.use(Router)
Vue.use(VueMeta)

const router = new Router({
  mode: "history",
  scrollBehavior (to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash,
      }
    }
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
  routes: [
    ...authRoutes,
    ...bootcampRoutes,
    ...coachHubRoutes,
    ...coachingRoutes,
    ...companyRoutes,
    ...coursesRoutes,
    ...landingRoutes,
    ...leagueOfLegendsRoutes,
    ...ssbuRoutes,
    ...storeRoutes,
    ...userRoutes,
    {
      path: "/dashboard/:game?",
      name: ROUTER_NAMES.DASHBOARD,
      component: Dashboard,
      meta: {
        layout: {
          component: MainLayout,
        },
      },
      beforeEnter: async (_to, from, next) => {
        // Verify logged in user
        // TODO: Middleware this
        try {
          await store.dispatch("User/loadUser")
        } catch (error) {}
        const user = store.getters["User/getUser"]
        if (!user) {
          return next("/register")
        }

        // Load data required for dashboard sections
        try {
          await store.dispatch("User/loadUserActiveGames")
        } catch (error) {}

        // If game is not specified in route, navigate to the User's active game
        const game = _to.params.game
        if (!game) {
          const currentActiveGame = store.getters["User/currentActiveGame"]
          return next({
            name: ROUTER_NAMES.DASHBOARD,
            query: _to.query,
            params: {
              game: currentActiveGame || GAME_KEYS.LEAGUE_OF_LEGENDS,
            },
          })
        }

        // Track Bootcamp Purchase
        // TODO: Handle in Bootcamp confirmation flow instead...?
        if (_to.query.bootcamp) {
          try {
            MIXINS.methods.trackAnalyticsEvent("purchase", {
              value: Number(_to.query.bootcamp),
            })
          } catch (error) {
            console.error(error)
          }
          return next("/bootcamp/confirm")
        }

        // Continue navigating to game if it is publicly available
        try {
          await store.dispatch("Games/loadGames")
        } catch (error) {}
        const isGameAvailable = store.getters["Games/getGameByKey"](game)
        if (isGameAvailable) {
          await store.dispatch("User/setUserActiveGame", game)
          return next()
        } else {
          return next("/404")
        }
      },
    },
    {
      path: "/",
      name: ROUTER_NAMES.HOME,
      component: Home,
      meta: {
        layout: {
          component: FooterLayout,
        },
      },
    },
    {
      path: "/404",
      name: ROUTER_NAMES.ERROR_404,
      component: Page404,
      meta: {
        layout: {
          component: MainLayout,
        },
      },
    },
    {
      path: "/:game",
      name: ROUTER_NAMES.HOME_GAME,
      component: Home,
      meta: {
        layout: {
          component: FooterLayout,
        },
      },
    },
    {
      path: "*",
      name: ROUTER_NAMES.ERROR_404,
      component: Page404,
      meta: {
        layout: {
          component: MainLayout,
        },
      },
    },
  ],
})

/**
 * Navigation Guards
 */
router.beforeEach(async (to, from, next) => {
  store.commit("Util/setPageLoading", true)

  /**
   * Handle any potential redirects
   */
  // Check if we're logging in a user via URL
  const queryParams = { ...to.query }
  const oneTimeToken = queryParams["one_time_token"]
  if (oneTimeToken) {
    try {
      await store.dispatch("Auth/fetchClientCredentialsWithOneTimeToken", oneTimeToken)
      return next()
    } catch (e) {
      // If authenticating user using the token fails for any reason, route user to Login page with the appropriate redirect link
      delete queryParams["one_time_token"]
      const queryParamString = Object.keys(queryParams).map(key => key + "=" + queryParams[key]).join("&")
      let postLoginRedirectPath = to.path
      if (queryParamString) {
        postLoginRedirectPath += `?${queryParamString}`
      }
      return next({
        name: ROUTER_NAMES.LOGIN,
        query: {
          from: postLoginRedirectPath,
        },
      })
    }
  }

  return next()
})

router.beforeResolve((to, from, next) => {
  /**
   * Prerender.io (SEO - SSR alternative)
   */
  // Tell Prerender.io to wait until we say its ready
  window.prerenderReady = false

  return next()
})

router.afterEach(async (to, from) => {
  // Clear any states that should not persist between route changes
  store.commit("Util/setShowGameContainer", false)
  store.commit("Util/setNavbarDrawerShowing", false)
  store.commit("Util/setPageLoading", false)

  /**
   * Track page load
   */
  // FB Pixel
  if (store.getters["Util/isPageStatic"](window.location.pathname)) {
    store.dispatch("Tracking/fbQuery", { event: "PageView", isStatic: true })
  } else {
    store.dispatch("Tracking/fbQuery", { event: "PageView" })
  }
  // Reddit Pixel
  store.dispatch("Tracking/trackRedditPixelEvent", { event: "PageVisit" })
  // analytics
  MIXINS.methods.trackAnalyticsPageView(to.name, {
    path: to.path,
    url: `${window.location.origin}${to.path}`,
    tab_url: `${window.location.origin}${to.path}`,
    title: "",
  })

  /**
   * Prerender.io (SEO - SSR alternative)
   * Assume page is ready/rendered after 1 second
   */
  setTimeout(() => {
    window.prerenderReady = true
  }, 1000)
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js